import { tracked } from "@glimmer/tracking";
import Controller from "@ember/controller";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { Promise } from "rsvp";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import discourseDebounce from "discourse/lib/debounce";
import { bind } from "discourse/lib/decorators";
import { INPUT_DELAY } from "discourse/lib/environment";
import { i18n } from "discourse-i18n";

export default class PluginsExplorerController extends Controller {
  @service dialog;
  @service appEvents;
  @service router;
  @service store;
  @service toasts;

  @tracked sortByProperty = "last_run_at";
  @tracked sortDescending = true;
  @tracked params;
  @tracked createFormData = { name: "" };
  @tracked showCreate;
  @tracked loading = false;
  @tracked searchLoading = false;

  queryParams = ["id"];
  explain = false;
  acceptedImportFileTypes = ["application/json"];
  order = null;
  form = null;
  _currentFilter = "";

  get parsedParams() {
    return this.params ? JSON.parse(this.params) : null;
  }

  addCreatedRecord(record) {
    this.model.content.push(record);
    this.router.transitionTo("adminPlugins.show.explorer.details", record.id);
  }

  async _importQuery(file) {
    const json = await this._readFileAsTextAsync(file);
    const query = this._parseQuery(json);
    const record = this.store.createRecord("query", query);
    const response = await record.save();
    return response.target;
  }

  _parseQuery(json) {
    const parsed = JSON.parse(json);
    const query = parsed.query;
    if (!query || !query.sql) {
      throw new TypeError();
    }
    query.id = 0; // 0 means no Id yet
    return query;
  }

  _readFileAsTextAsync(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = reject;

      reader.readAsText(file);
    });
  }

  @bind
  dragMove(e) {
    if (!e.movementY && !e.movementX) {
      return;
    }

    const editPane = document.querySelector(".query-editor");
    const target = editPane.querySelector(".panels-flex");
    const grippie = editPane.querySelector(".grippie");

    // we need to get the initial height / width of edit pane
    // before we manipulate the size
    if (!this.initialPaneWidth && !this.originalPaneHeight) {
      this.originalPaneWidth = target.clientWidth;
      this.originalPaneHeight = target.clientHeight;
    }

    const newHeight = Math.max(
      this.originalPaneHeight,
      target.clientHeight + e.movementY
    );
    const newWidth = Math.max(
      this.originalPaneWidth,
      target.clientWidth + e.movementX
    );

    target.style.height = newHeight + "px";
    target.style.width = newWidth + "px";
    grippie.style.width = newWidth + "px";
    this.appEvents.trigger("ace:resize");
  }

  @bind
  scrollTop() {
    window.scrollTo(0, 0);
  }

  @action
  async import(files) {
    try {
      this.loading = true;
      const file = files[0];
      const record = await this._importQuery(file);
      this.addCreatedRecord(record);
    } catch (e) {
      if (e.jqXHR) {
        popupAjaxError(e);
      } else if (e instanceof SyntaxError) {
        this.dialog.alert(i18n("explorer.import.unparseable_json"));
      } else if (e instanceof TypeError) {
        this.dialog.alert(i18n("explorer.import.wrong_json"));
      } else {
        this.dialog.alert(i18n("errors.desc.unknown"));
        // eslint-disable-next-line no-console
        console.error(e);
      }
    } finally {
      this.loading = false;
    }
  }

  @action
  displayCreate() {
    this.showCreate = true;
    this.createFormData = { name: "" };
  }

  @action
  hideCreate() {
    this.showCreate = false;
  }

  @action
  onTextFilterChange(event) {
    this._currentFilter = event.target?.value || "";
    this.searchLoading = true;
    discourseDebounce(this, this._fetchQueries, INPUT_DELAY);
  }

  @action
  onResetFilters() {
    this._currentFilter = "";
    this.searchLoading = true;
    this._fetchQueries();
  }

  @action
  updateSortProperty(property) {
    if (this.sortByProperty === property) {
      this.sortDescending = !this.sortDescending;
    } else {
      this.sortByProperty = property;
      this.sortDescending = true;
    }
    this.searchLoading = true;
    this._fetchQueries();
  }

  @action
  async loadMore() {
    const loadedMore = await this.model?.loadMore();
    if (loadedMore) {
      this._setGroupNames(this.model.content);
    }
  }

  get _fetchParams() {
    const params = {};
    if (this._currentFilter) {
      params.filter = this._currentFilter;
    }
    if (this.sortByProperty !== "last_run_at") {
      params.order = this.sortByProperty;
    }
    if (!this.sortDescending) {
      params.ascending = "true";
    }
    return params;
  }

  async _fetchQueries() {
    try {
      const result = await ajax(
        "/admin/plugins/discourse-data-explorer/queries.json",
        { data: this._fetchParams }
      );

      const queries = result.queries.map((q) =>
        this.store.createRecord("query", q)
      );

      this.model.content.splice(0, this.model.content.length, ...queries);
      this.model.totalRows = result.total_rows_queries || queries.length;
      this.model.loadMoreUrl = result.load_more_queries || null;

      this._setGroupNames(queries);
    } catch (e) {
      popupAjaxError(e);
    } finally {
      this.searchLoading = false;
    }
  }

  _setGroupNames(queries) {
    if (!this.groups) {
      return;
    }
    const groupNames = {};
    this.groups.forEach((g) => {
      groupNames[g.id] = g.name;
    });
    queries.forEach((query) => {
      query.set(
        "group_names",
        (query.group_ids || []).map((id) => groupNames[id])
      );
    });
  }

  @action
  async create({ name }) {
    try {
      this.loading = true;
      const result = await this.store
        .createRecord("query", { name: name.trim() })
        .save();
      this.toasts.success({
        data: { message: i18n("explorer.query_created") },
      });
      this.showCreate = false;
      this.createFormData = { name: "" };
      this.addCreatedRecord(result.target);
    } catch (error) {
      popupAjaxError(error);
    } finally {
      this.loading = false;
    }
  }
}
